import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DatePicker, Form, Input, InputNumber, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useCreateItem } from "@/modules/items/hooks/useItems";
import { itemsService } from "@/modules/items/services/items.service";
import { personsService } from "@/modules/persons/services/persons.service";
import { usersService } from "@/modules/users/services/users.service";
import type { AidRecord, AidRecordCreateRequest } from "../types";

const CREATE_ITEM_VALUE = "__create_new_item__";

// Keeps the calendar popup below the field. The default bottomLeft placement
// flips the whole popup above the input when the viewport is short, which made
// it cover the modal header. `adjustY` must be `false` — rc-trigger treats any
// number as "enabled" (`val >= 0`), so `0` would not have disabled the flip.
// `shiftY` then nudges the popup just enough to stay inside the viewport.
const DATE_PICKER_PLACEMENTS = {
	bottomLeft: {
		points: ["tl", "bl"] as ["tl", "bl"],
		offset: [0, 4] as [number, number],
		overflow: { adjustX: true, adjustY: false, shiftX: true, shiftY: true },
	},
};

interface AidRecordFormValues {
	givenAt: dayjs.Dayjs;
	givenById: string;
	personId: string;
	itemId: string;
	quantity: number;
	procedure?: string;
	comment?: string;
}

interface AidRecordFormModalProps {
	open: boolean;
	editing: AidRecord | null;
	loading: boolean;
	onCancel: () => void;
	onSubmit: (values: AidRecordCreateRequest) => void;
}

function useSearchOptions(searchFn: (search: string) => Promise<{ id: string; name: string }[]>) {
	const [search, setSearch] = useState("");
	const { data, isFetching } = useQuery({
		queryKey: ["search-options", searchFn.name, search],
		queryFn: () => searchFn(search),
	});

	return { options: data ?? [], isFetching, onSearch: setSearch, search };
}

async function searchPersons(search: string) {
	const result = await personsService.list({ page: 1, limit: 20, fullName: search || undefined });
	return result.items.map((p) => ({ id: p.id, name: p.fullName }));
}

async function searchItems(search: string) {
	const result = await itemsService.list({ page: 1, limit: 20, name: search || undefined });
	return result.items.map((i) => ({ id: i.id, name: `${i.name} (${i.unit})` }));
}

// `givenById` refers to a staff user account, not a person record — the two are
// separate tables on the backend. GET /users only filters by phone, so a name
// search is applied client-side over a small page.
async function searchUsers(search: string) {
	const isPhoneLike = /\d/.test(search);
	const result = await usersService.list({
		page: 1,
		limit: 50,
		phone: isPhoneLike ? search : undefined,
	});
	const term = search.trim().toLowerCase();
	const items = term
		? result.items.filter((u) => u.fullName.toLowerCase().includes(term))
		: result.items;
	// A user's fullName can be blank (e.g. the seeded admin account) — without a
	// fallback, AntD's Select renders the raw option value (a UUID) instead.
	return items.map((u) => ({ id: u.id, name: u.fullName || u.phone || "Noma'lum xodim" }));
}

export function AidRecordFormModal({
	open,
	editing,
	loading,
	onCancel,
	onSubmit,
}: AidRecordFormModalProps) {
	const queryClient = useQueryClient();
	const [form] = Form.useForm<AidRecordFormValues>();
	const persons = useSearchOptions(searchPersons);
	const items = useSearchOptions(searchItems);
	const users = useSearchOptions(searchUsers);

	const [newItemForm] = Form.useForm<{ name: string; unit: string }>();
	const [newItemModalOpen, setNewItemModalOpen] = useState(false);
	const [createdItem, setCreatedItem] = useState<{ id: string; name: string } | null>(null);
	const createItemMutation = useCreateItem();
	const itemIdBeforeCreateRef = useRef<string | undefined>(undefined);

	const openNewItemModal = () => {
		itemIdBeforeCreateRef.current = form.getFieldValue("itemId");
		newItemForm.setFieldsValue({ name: items.search, unit: "" });
		setNewItemModalOpen(true);
		// Select commits CREATE_ITEM_VALUE into the form store synchronously right
		// after onSelect fires — revert it once that update has landed.
		setTimeout(() => form.setFieldValue("itemId", itemIdBeforeCreateRef.current), 0);
	};

	const handleCreateItem = async () => {
		const values = await newItemForm.validateFields();
		const item = await createItemMutation.mutateAsync(values);
		setCreatedItem({ id: item.id, name: `${item.name} (${item.unit})` });
		form.setFieldValue("itemId", item.id);
		setNewItemModalOpen(false);
		items.onSearch("");
		queryClient.invalidateQueries({ queryKey: ["search-options", "searchItems"] });
	};

	useEffect(() => {
		if (open) {
			setCreatedItem(null);
			form.setFieldsValue({
				givenAt: editing ? dayjs(editing.givenAt) : dayjs(),
				givenById: editing?.givenBy.id,
				personId: editing?.person.id,
				itemId: editing?.item.id,
				quantity: editing?.quantity ?? 1,
				procedure: editing?.procedure ?? undefined,
				comment: editing?.comment ?? undefined,
			});
		}
	}, [open, editing, form]);

	const handleOk = async () => {
		const values = await form.validateFields();
		onSubmit({
			...values,
			givenAt: values.givenAt.toISOString(),
		});
	};

	const toOptions = (
		list: { id: string; name: string }[],
		...currents: ({ id: string; name: string } | null | undefined)[]
	) => {
		const options = list.map((o) => ({ value: o.id, label: o.name || "Noma'lum" }));
		for (const current of currents) {
			if (current && !options.some((o) => o.value === current.id)) {
				options.unshift({ value: current.id, label: current.name || "Noma'lum" });
			}
		}
		return options;
	};

	const itemOptions = toOptions(items.options, editing?.item, createdItem ?? undefined);
	if (items.search.trim()) {
		itemOptions.push({
			value: CREATE_ITEM_VALUE,
			label: `+ "${items.search.trim()}" nomli yangi mahsulot yaratish`,
		});
	}

	return (
		<Modal
			title={editing ? "Yozuvni tahrirlash" : "Yangi yordam yozuvi"}
			open={open}
			onCancel={onCancel}
			onOk={handleOk}
			okText={editing ? "Saqlash" : "Qo'shish"}
			cancelText="Bekor qilish"
			confirmLoading={loading}
			destroyOnHidden
			centered
			width={560}
			styles={{
				body: {
					maxHeight: "calc(78vh - 80px)",
					overflowY: "auto",
					overflowX: "hidden",
				},
			}}
		>
			<Form form={form} layout="vertical" className="mt-3" requiredMark={false}>
				<div className="grid grid-cols-1 gap-x-4 gap-y-0 sm:grid-cols-2">
					<Form.Item
						name="personId"
						label="Kimga berildi"
						rules={[{ required: true, message: "Shaxsni tanlang" }]}
					>
						<Select
							showSearch
							filterOption={false}
							placeholder="Shaxsni qidirish"
							notFoundContent={persons.isFetching ? "Qidirilmoqda…" : "Topilmadi"}
							loading={persons.isFetching}
							onSearch={persons.onSearch}
							options={toOptions(persons.options, editing?.person)}
						/>
					</Form.Item>
					<Form.Item
						name="givenById"
						label="Kim berdi (xodim)"
						rules={[{ required: true, message: "Xodimni tanlang" }]}
					>
						<Select
							showSearch
							filterOption={false}
							placeholder="Xodimni qidirish"
							notFoundContent={users.isFetching ? "Qidirilmoqda…" : "Topilmadi"}
							loading={users.isFetching}
							onSearch={users.onSearch}
							options={toOptions(users.options, editing?.givenBy)}
						/>
					</Form.Item>
					<Form.Item
						name="itemId"
						label="Mahsulot"
						rules={[{ required: true, message: "Mahsulotni tanlang" }]}
					>
						<Select
							showSearch
							filterOption={false}
							placeholder="Mahsulotni qidirish"
							notFoundContent={items.isFetching ? "Qidirilmoqda…" : "Topilmadi"}
							loading={items.isFetching}
							onSearch={items.onSearch}
							onSelect={(value) => {
								if (value === CREATE_ITEM_VALUE) {
									openNewItemModal();
								}
							}}
							options={itemOptions}
						/>
					</Form.Item>
					<Form.Item
						name="quantity"
						label="Miqdori"
						rules={[{ required: true, message: "Miqdorni kiriting" }]}
					>
						<InputNumber min={1} max={2_147_483_647} className="w-full" />
					</Form.Item>
					<Form.Item name="givenAt" label="Berilgan vaqt">
						<DatePicker
							showTime
							format="DD.MM.YYYY HH:mm"
							className="w-full"
							placement="bottomLeft"
							builtinPlacements={DATE_PICKER_PLACEMENTS}
						/>
					</Form.Item>
					<Form.Item name="procedure" label="Tartib">
						<Input placeholder="masalan: Qo'lda topshirildi" />
					</Form.Item>
				</div>
				<Form.Item name="comment" label="Izoh" className="!mb-0">
					<Input.TextArea rows={2} placeholder="Qo'shimcha ma'lumot (majburiy emas)" />
				</Form.Item>
			</Form>

			<Modal
				title="Yangi mahsulot yaratish"
				open={newItemModalOpen}
				onCancel={() => setNewItemModalOpen(false)}
				onOk={handleCreateItem}
				okText="Yaratish"
				cancelText="Bekor qilish"
				confirmLoading={createItemMutation.isPending}
				destroyOnHidden
			>
				<Form form={newItemForm} layout="vertical" className="mt-4" requiredMark={false}>
					<Form.Item
						name="name"
						label="Nomi"
						rules={[{ required: true, message: "Nomi kiritilishi shart" }]}
					>
						<Input placeholder="masalan: Un" autoFocus />
					</Form.Item>
					<Form.Item
						name="unit"
						label="O'lchov birligi"
						rules={[{ required: true, message: "O'lchov birligi kiritilishi shart" }]}
						extra="Masalan: kg, dona, litr"
					>
						<Input placeholder="kg" />
					</Form.Item>
				</Form>
			</Modal>
		</Modal>
	);
}
